import { computed, ref } from 'vue';

/**
 * Shared file picker state for desk composer and AI draft bubble.
 *
 * @param {{ maxAttachmentMb: import('vue').Ref<number>|number, onReject?: (message: string) => void, labels?: import('vue').Ref<object>|object }} options
 */
export function useAttachmentPicker(options) {
    const fileInputEl = ref(null);
    const selectedFiles = ref([]);

    const maxAttachmentBytes = computed(() => {
        const mb = typeof options.maxAttachmentMb === 'object' && options.maxAttachmentMb?.value != null
            ? options.maxAttachmentMb.value
            : options.maxAttachmentMb;
        return Math.max(1, Number(mb) || 25) * 1024 * 1024;
    });

    const fileKey = (file) => [file.name, file.size, file.lastModified].join('-');

    const clearSelectedFiles = () => {
        selectedFiles.value = [];
        if (fileInputEl.value) {
            fileInputEl.value.value = '';
        }
    };

    const rejectMessage = () => {
        const labels = typeof options.labels === 'object' && options.labels?.value != null
            ? options.labels.value
            : (options.labels || {});
        const mb = typeof options.maxAttachmentMb === 'object' && options.maxAttachmentMb?.value != null
            ? options.maxAttachmentMb.value
            : options.maxAttachmentMb;
        return labels.attachmentTooLarge || `Allegato troppo grande (max ${mb || 25} MB)`;
    };

    const onFilesPicked = (event) => {
        const picked = Array.from(event.target.files || []);
        const maxBytes = maxAttachmentBytes.value;
        const next = selectedFiles.value.slice();
        let rejected = false;

        picked.forEach((file) => {
            if (file.size > maxBytes) {
                rejected = true;
                return;
            }
            const key = fileKey(file);
            if (!next.some((existing) => fileKey(existing) === key)) {
                next.push(file);
            }
        });

        selectedFiles.value = next;
        if (fileInputEl.value) {
            fileInputEl.value.value = '';
        }
        if (rejected && options.onReject) {
            options.onReject(rejectMessage());
        }
    };

    const removeFile = (key) => {
        selectedFiles.value = selectedFiles.value.filter((file) => fileKey(file) !== key);
    };

    const openFilePicker = () => {
        fileInputEl.value?.click();
    };

    return {
        fileInputEl,
        selectedFiles,
        fileKey,
        clearSelectedFiles,
        onFilesPicked,
        removeFile,
        openFilePicker,
    };
}
